import type { AsyncStorage, SyncStorage } from "@/context/platform"
import { checksum } from "@/utils/encode"
import { createResource, onCleanup, untrack, type Accessor } from "solid-js"
import { reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { showToast } from "@mux/ui/toast"
import { createPromptRepository } from "./prompt-repository"

type InitType = Promise<string> | string | null
type PersistedWithReady<T> = [Store<T>, SetStoreFunction<T>, InitType, Accessor<boolean>]

type PersistTarget = {
  storage?: string
  // One atomic snapshot; independently editable preferences need distinct keys.
  key: string
  migrate?: (value: unknown) => unknown
  project?: (value: unknown) => unknown
  writeDelayMs?: number
  kind: "device-preference" | "draft" | "cache"
}

const GLOBAL_STORAGE = "mux.global.dat"
// Unknown keys are protected. Only explicitly registered disposable
// caches can be reclaimed, never preferences, drafts, or credentials.
const disposableKeys = new Set<string>()

const CACHE_MAX_ENTRIES = 500
const CACHE_MAX_BYTES = 8 * 1024 * 1024

type CacheEntry = { value: string; bytes: number }
const cache = new Map<string, CacheEntry>()
const cacheTotal = { bytes: 0 }

function cacheDelete(key: string) {
  const entry = cache.get(key)
  if (!entry) return
  cacheTotal.bytes -= entry.bytes
  cache.delete(key)
}

function cachePrune() {
  for (;;) {
    if (cache.size <= CACHE_MAX_ENTRIES && cacheTotal.bytes <= CACHE_MAX_BYTES) return
    const oldest = cache.keys().next().value as string | undefined
    if (!oldest) return
    cacheDelete(oldest)
  }
}

function cacheSet(key: string, value: string) {
  const bytes = value.length * 2
  if (bytes > CACHE_MAX_BYTES) {
    cacheDelete(key)
    return
  }

  const entry = cache.get(key)
  if (entry) cacheTotal.bytes -= entry.bytes
  cache.delete(key)
  cache.set(key, { value, bytes })
  cacheTotal.bytes += bytes
  cachePrune()
}

function cacheGet(key: string) {
  const entry = cache.get(key)
  if (!entry) return
  cache.delete(key)
  cache.set(key, entry)
  return entry.value
}

function quota(error: unknown) {
  if (error instanceof DOMException) {
    if (error.name === "QuotaExceededError") return true
    if (error.name === "NS_ERROR_DOM_QUOTA_REACHED") return true
    if (error.name === "QUOTA_EXCEEDED_ERR") return true
    if (error.code === 22 || error.code === 1014) return true
    return false
  }

  if (!error || typeof error !== "object") return false
  const name = (error as { name?: string }).name
  if (name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED") return true
  if (name && /quota/i.test(name)) return true

  const code = (error as { code?: number }).code
  if (code === 22 || code === 1014) return true

  const message = (error as { message?: string }).message
  if (typeof message !== "string") return false
  if (/quota/i.test(message)) return true
  return false
}

type Evict = { key: string; size: number }

function evict(storage: Storage, keep: string, value: string) {
  const total = storage.length
  const indexes = Array.from({ length: total }, (_, index) => index)
  const items: Evict[] = []

  for (const index of indexes) {
    const name = storage.key(index)
    if (!name) continue
    if (!disposableKeys.has(name)) continue
    if (name === keep) continue
    const stored = storage.getItem(name)
    items.push({ key: name, size: stored?.length ?? 0 })
  }

  items.sort((a, b) => b.size - a.size)

  for (const item of items) {
    storage.removeItem(item.key)
    cacheDelete(item.key)
    disposableKeys.delete(item.key)

    try {
      storage.setItem(keep, value)
      cacheSet(keep, value)
      return true
    } catch (error) {
      if (!quota(error)) throw error
    }
  }

  return false
}

function write(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value)
    cacheSet(key, value)
    return true
  } catch (error) {
    if (!quota(error)) throw error
  }

  if (evict(storage, key, value)) return true
  throw new Error("Browser storage is full. Free space and try saving again.")
}

function snapshot(value: unknown) {
  return JSON.parse(JSON.stringify(value)) as unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function merge(defaults: unknown, value: unknown): unknown {
  if (value === undefined) return defaults
  if (value === null) return value

  if (Array.isArray(defaults)) {
    if (Array.isArray(value)) return value
    return defaults
  }

  if (isRecord(defaults)) {
    if (!isRecord(value)) return defaults

    const result: Record<string, unknown> = { ...defaults }
    for (const key of Object.keys(value)) {
      if (key in defaults) {
        result[key] = merge((defaults as Record<string, unknown>)[key], (value as Record<string, unknown>)[key])
      } else {
        result[key] = (value as Record<string, unknown>)[key]
      }
    }
    return result
  }

  return value
}

function parse(value: string) {
  try {
    return JSON.parse(value) as unknown
  } catch {
    return undefined
  }
}

function normalize(defaults: unknown, raw: string, migrate?: (value: unknown) => unknown) {
  const parsed = parse(raw)
  if (parsed === undefined) return
  const migrated = migrate ? migrate(parsed) : parsed
  const merged = merge(defaults, migrated)
  return JSON.stringify(merged)
}

function workspaceStorage(dir: string) {
  const head = (dir.slice(0, 12) || "workspace").replace(/[^a-zA-Z0-9._-]/g, "-")
  const sum = checksum(dir) ?? "0"
  return `mux.workspace.${head}.${sum}.dat`
}

function localStorageAdapter(prefix: string, kind: PersistTarget["kind"], report: (error: unknown) => void): SyncStorage {
  const item = (key: string) => prefix + key
  const register = (name: string) => {
    if (kind !== "cache") { disposableKeys.delete(name); return }
    disposableKeys.add(name)
    if (disposableKeys.size > CACHE_MAX_ENTRIES) disposableKeys.delete(disposableKeys.values().next().value!)
  }
  return {
    getItem(key) {
      const name = item(key)
      register(name)
      try {
        const value = localStorage.getItem(name)
        if (value === null) cacheDelete(name)
        else cacheSet(name, value)
        return value
      } catch (error) {
        report(error)
        return cacheGet(name) ?? null
      }
    },
    setItem(key, value) {
      const name = item(key)
      register(name)
      try { write(localStorage, name, value) } catch (error) { report(error) }
    },
    removeItem(key) {
      const name = item(key)
      try {
        localStorage.removeItem(name)
        cacheDelete(name)
      } catch (error) { report(error) }
    },
  }
}

function localStorageWithPrefix(prefix: string, kind: PersistTarget["kind"] = "cache", report: (error: unknown) => void = () => {}) {
  return localStorageAdapter(`${prefix}:`, kind, report)
}

function localStorageDirect(kind: PersistTarget["kind"] = "cache", report: (error: unknown) => void = () => {}) {
  return localStorageAdapter("", kind, report)
}

export const PersistTesting = {
  localStorageDirect,
  localStorageWithPrefix,
  normalize,
  workspaceStorage,
}

function workspaceTarget(kind: PersistTarget["kind"], dir: string, key: string): PersistTarget {
  return { storage: workspaceStorage(dir), key: `workspace:${key}`, kind }
}

function sessionTarget(kind: PersistTarget["kind"], dir: string, session: string, key: string): PersistTarget {
  return { storage: workspaceStorage(dir), key: `session:${session}:${key}`, kind }
}

function targets(kind: PersistTarget["kind"]) {
  return {
    global(key: string): PersistTarget {
      return { storage: GLOBAL_STORAGE, key, kind }
    },
    workspace(dir: string, key: string): PersistTarget {
      return workspaceTarget(kind, dir, key)
    },
    session(dir: string, session: string, key: string): PersistTarget {
      return sessionTarget(kind, dir, session, key)
    },
    scoped(dir: string, session: string | undefined, key: string): PersistTarget {
      return session ? sessionTarget(kind, dir, session, key) : workspaceTarget(kind, dir, key)
    },
  }
}

/** Durable state must declare why it is local. Shared server state is not allowed here. */
export const DevicePreference = targets("device-preference")
export const LocalDraft = targets("draft")
export const RevisionedCache = targets("cache")

export async function removePersisted(target: Pick<PersistTarget, "storage" | "key">) {
  if (target.storage) {
    localStorageWithPrefix(target.storage).removeItem(target.key)
  } else {
    localStorageDirect().removeItem(target.key)
  }
}

export async function clearWorkspaceStorage(dir: string) {
  const prefix = workspaceStorage(dir)
  const base = `${prefix}:`
  const prompts = createPromptRepository()
  try {
    await prompts.removeDrafts(base)
  } finally {
    prompts.dispose()
  }

  for (const key of cache.keys()) {
    if (key.startsWith(base)) cacheDelete(key)
  }

  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(base)) keysToRemove.push(key)
    }
    for (const key of keysToRemove) localStorage.removeItem(key)
  } catch (e) {
    console.warn("Failed to clear workspace localStorage", e)
  }
}


/**
 * Loads `key` from `storage` into the store, then writes the whole store as
 * JSON under `key` after every set. An async load is ignored once the store
 * has been set. Unparsable stored JSON leaves the store's defaults.
 */
function persistStore<T>(
  [state, setState]: [Store<T>, SetStoreFunction<T>],
  key: string,
  storage: SyncStorage | AsyncStorage,
): [Store<T>, SetStoreFunction<T>, InitType] {
  const load = (raw: string) => {
    const value = parse(raw)
    if (value !== undefined) setState(reconcile(value as T))
  }
  let changed = false
  const init = storage.getItem(key)
  if (init instanceof Promise) void init.then((raw) => !changed && raw && load(raw))
  else if (init) load(init)

  const set = ((...args: unknown[]) => {
    ;(setState as (...args: unknown[]) => void)(...args)
    void storage.setItem(key, JSON.stringify(untrack(() => state)))
    changed = true
  }) as SetStoreFunction<T>
  return [state, set, init]
}

export function persisted<T>(
  target: PersistTarget,
  store: [Store<T>, SetStoreFunction<T>],
): PersistedWithReady<T> {
  const config = target

  const defaults = snapshot(store[0])

  let reportedFailure = false
  let writeFailed = false
  const report = () => {
    writeFailed = true
    if (config.kind === "cache" || reportedFailure) return
    reportedFailure = true
    showToast({ title: "Could not persist browser data", description: "Changes may be lost on refresh. Check browser storage permissions and available space, then try again." })
  }
  const current = config.storage
    ? localStorageWithPrefix(config.storage, config.kind, report)
    : localStorageDirect(config.kind, report)

  const encode = (value: string) => {
    if (!config.project) return value
    const parsed = parse(value)
    if (parsed === undefined) return value
    return JSON.stringify(config.project(parsed))
  }

  const storage: SyncStorage = {
    getItem: (key) => {
      const raw = current.getItem(key)
      if (raw === null) return null
      const next = normalize(defaults, raw, config.migrate)
      if (next === undefined) {
        current.removeItem(key)
        return null
      }
      const stored = encode(next)
      if (raw !== stored) current.setItem(key, stored)
      return next
    },
    setItem: (key, value) => {
      writeFailed = false
      current.setItem(key, encode(value))
      if (!writeFailed) reportedFailure = false
    },
    removeItem: (key) => {
      current.removeItem(key)
    },
  }

  const persistedStorage = (() => {
    if (!config.writeDelayMs || config.writeDelayMs <= 0) return storage

    let timer: ReturnType<typeof setTimeout> | undefined
    let pendingKey: string | undefined

    const flush = () => {
      if (timer !== undefined) {
        clearTimeout(timer)
        timer = undefined
      }
      const key = pendingKey
      pendingKey = undefined
      if (!key) return
      void storage.setItem(key, JSON.stringify(snapshot(store[0])))
    }

    const schedule = (key: string) => {
      pendingKey = key
      if (timer !== undefined) clearTimeout(timer)
      timer = setTimeout(flush, config.writeDelayMs)
    }

    const remove = (key: string) => {
      if (pendingKey === key) pendingKey = undefined
      if (timer !== undefined) {
        clearTimeout(timer)
        timer = undefined
      }
      return storage.removeItem(key)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush()
    }
    window.addEventListener("pagehide", flush)
    document.addEventListener("visibilitychange", onVisibilityChange)
    onCleanup(() => {
      window.removeEventListener("pagehide", flush)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      flush()
    })

    const delayed: SyncStorage = {
      getItem: (key) => storage.getItem(key),
      setItem: schedule,
      removeItem: remove,
    }
    return delayed
  })()

  const [state, setState, init] = persistStore(store, config.key, persistedStorage)

  const isAsync = init instanceof Promise
  const [ready] = createResource(
    () => init,
    async (initValue) => {
      if (initValue instanceof Promise) await initValue
      return true
    },
    { initialValue: !isAsync },
  )

  return [state, setState, init, () => ready() === true]
}
